/**
 * @file Selfie Capture component.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniqby from 'lodash.uniqby';
import store from 'store';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import Button from '../Button';
import getLogger from '../../../util/logger';
import { getAsset } from '../../../util/assets';
import {
  getSelfieCaptureError,
  dataURItoFile,
  getFaceMaskConfig,
  FACE_PATH_DIMENSION,
} from './utilities';
import { SELFIE_CAPTURE_ERROR_CODES } from './constants';
import SelfieCaptureFaceMask from './SelfieCaptureFaceMask';
import { requestConfirm as requestConfirmAction } from '../../../actions/modal-actions';
import uuidv4 from '../../../util/uuid';
import AngleLeftIcon from '../../icons/AngleLeftIcon';
import AngleRightIcon from '../../icons/AngleRightIcon';
import { STORE_NAMESPACE } from '../DismissiblePersistent';
import SelfieCaptureCountdown from './SelfieCaptureCountdown';
import ScrollToTop from '../../routing/ScrollToTop';
import { isIOS } from '../../../util/device-detector';
import { getScreenResolution } from '../../../util/screen';
import { getErrorString } from '../../../util/normalize-errors';
import CameraIcon from '../../icons/CameraIcon';

const logger = getLogger('SelfieCapture');

class SelfieCapture extends Component {
  static propTypes = {
    onSelfieConfirmed: PropTypes.func,
    onSelfieTaken: PropTypes.func,
    onStreamStarted: PropTypes.func,
    onError: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    requestConfirm: PropTypes.func.isRequired,
    /**
     * When provided, the choice for Camera will be stored
     * with this key.
     */
    storeKey: PropTypes.string,
    countdown: PropTypes.number,
    renderPermissionButton: PropTypes.func,
    renderCaptureAction: PropTypes.func,
    faceMaskDisabled: PropTypes.bool,
    faceMaskProps: PropTypes.shape({
      resolution: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
      }),
    }),
    includeTipsCopy: PropTypes.bool,
  };

  static defaultProps = {
    includeTipsCopy: true,
  };

  state = {
    selfie: null,
    // Whether we got camera permission from the confirm modal
    cameraPermission: false,
    // Initially set this to true until we actually attempt
    // to use WebRTC capabilities
    loadingDevices: true,
    devices: [],
    currentDevice: null,
    // Forces an update after the component was mounted on the client
    // This because SSR loads dimensions wrong and does not allow proper
    // manipulation of the face mask.

    client: false,
    mirrored: true,
    countdownStarted: false,
  };

  cameraStream = null;

  cameraStreamEl = null;

  resultPhotoEl = null;

  componentDidMount() {
    const { renderPermissionButton } = this.props;
    this.setState(() => ({ client: true }));
    // If the render permission button is not present, automatically request permission automatically
    if (!renderPermissionButton) {
      this.getCameraPermission();
    }
  }

  componentWillUnmount() {
    this.stopTracks();
  }

  stopTracks = () => {
    if (!this.cameraStream) {
      return;
    }

    this.cameraStream.getTracks().forEach(track => track.stop());
  };

  getCameraPermission = () => {
    const { cameraPermission } = this.state;
    const { storeKey } = this.props;

    const localStore = store.get(STORE_NAMESPACE) || {};
    const { grantedCameraPermission = [] } = localStore;

    const hasGrantedPermission = storeKey
      ? grantedCameraPermission.includes(storeKey)
      : false;

    if (cameraPermission || hasGrantedPermission) {
      this.initSelfieStream();
      return;
    }

    const { requestConfirm, onError } = this.props;

    requestConfirm({
      message: 'Please grant access to camera below so I can take a photo!',
      confirmText: 'Yes',
      cancelText: 'No',
      onCancel: () => {
        onError(getSelfieCaptureError(SELFIE_CAPTURE_ERROR_CODES.SC001));
      },
      onConfirm: async () => {
        this.setState(
          () => ({ cameraPermission: true }),
          () => {
            if (storeKey) {
              store.set(STORE_NAMESPACE, {
                ...localStore,
                grantedCameraPermission: [...grantedCameraPermission, storeKey],
              });
            }

            this.initSelfieStream();
          },
        );
      },
    });
  };

  getDevices = async () => {
    const { currentDevice } = this.state;
    const { onError } = this.props;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = uniqby(
        devices.filter(device => device.kind === 'videoinput'),
        device => device.deviceId,
      );

      if (videoDevices.length === 0) {
        throw getSelfieCaptureError(SELFIE_CAPTURE_ERROR_CODES.SC002);
      }

      logger.info('getDevices() :: ', videoDevices);

      // Based on the devices gotten, fill in a current device
      let nextDevice = null;
      const frontCamera = videoDevices.find(device =>
        device.label.toLowerCase().includes('front'),
      );

      // Useful for Android specially, which names their cameras front & back
      if (frontCamera) {
        nextDevice = frontCamera;
      }

      // in iOS, 1st device seems to be the back camera. 2nd is Front from what we've
      // been able to test, which is why we'll assume this to be the default device.
      // --
      // As of 2023 - it seems new iOS devices do label the front camera as "Front".
      // Since that's the case, we're adding an extra check just to ensure this happens
      // only if the frontCamera is not found.
      if (isIOS() && videoDevices.length >= 2 && !frontCamera) {
        nextDevice = videoDevices[1];
      }

      if (currentDevice) {
        nextDevice = currentDevice;
      }

      if (!nextDevice) {
        [nextDevice] = videoDevices;
      }

      this.setState(() => ({
        devices: videoDevices,
        currentDevice: nextDevice,
      }));
    } catch (error) {
      const selfieCaptureError =
        getSelfieCaptureError(error.name) || getErrorString(error);
      onError(selfieCaptureError);
      throw error;
    }
  };

  // Video dimensions according to the video element.
  getVideoStreamDimensions = () => {
    if (!this.cameraStreamEl) {
      return {};
    }

    const { videoWidth: width, videoHeight: height } = this.cameraStreamEl;

    return { width, height };
  };

  // This is, according to the MediaTrack object, width & height of the track,
  getVideoTrackDimensions = () => {
    if (!this.cameraStream) {
      return {};
    }

    const { width, height } = this.cameraStream
      .getVideoTracks()[0]
      .getSettings();

    return { width, height };
  };

  initSelfieStream = async () => {
    const { devices, currentDevice } = this.state;
    const { onError, onStreamStarted } = this.props;

    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => {
        track.stop();
      });

      this.cameraStream = null;
    }

    const constraints = {
      video: {
        // Passing undefined will make it user `facingMode` & guess a device
        // Most of these constraints go for ideal values, so it should not fail at all
        // unless no cameras are found whatsoever.
        deviceId: currentDevice?.deviceId
          ? { exact: currentDevice.deviceId }
          : undefined,
        width: { min: 320, ideal: 1980, max: 1920 },
        height: { min: 240, ideal: 1080, max: 1080 },
        facingMode: 'user',
      },
      audio: false,
    };

    this.setState(() => ({
      loadingDevices: true,
    }));

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // we must get devices *after* requesting permissions, otherwise iOS
      // will not retrieve the full set of available devices.
      if (!devices.length) {
        await this.getDevices();
      }

      this.cameraStream = stream;
      this.cameraStreamEl.srcObject = stream;

      const { width, height } = this.getVideoTrackDimensions();

      logger.info(`initSelfieStream :: Video Track Resolution: `, {
        width,
        height,
      });
      this.setState(() => ({ loadingDevices: false }));
      onStreamStarted?.();
    } catch (error) {
      const selfieCaptureError =
        getSelfieCaptureError(error.name) || getErrorString(error);
      onError(selfieCaptureError);
    }
  };

  onDeviceChange = () => {
    const { devices, currentDevice } = this.state;

    const currentDeviceIndex = devices
      .map(device => device.deviceId)
      .indexOf(currentDevice.deviceId);

    const nextDevice = devices[currentDeviceIndex + 1] || devices[0];

    this.setState(
      () => ({ currentDevice: nextDevice }),
      () => {
        this.initSelfieStream();
      },
    );
  };

  onSelfieCapture = () => {
    const { onSelfieTaken, countdown, crop = true } = this.props;
    const { selfie, mirrored, countdownStarted } = this.state;

    if (selfie) {
      this.setState(() => ({ selfie: null }));
      return;
    }

    if (countdown && !countdownStarted) {
      // start countdown:
      this.setState(() => ({ countdownStarted: true }));
      return;
    }
    // We use video dimensions rather than stream dimensions
    // because it is possible that we got a 1080p stream that's
    // being displayed vertically (the case for mobile phones for example).
    // In those cases, we want to still get the width/height of our photos right.
    const videoDimensions = this.getVideoStreamDimensions();

    const { scale } = getFaceMaskConfig(videoDimensions);
    const faceMaskDimensions = {
      width: FACE_PATH_DIMENSION.width * scale,
      height: FACE_PATH_DIMENSION.height * scale,
    };

    // If crop is not specified, take the picture based off the video source
    const width = crop
      ? Math.min(videoDimensions.width, faceMaskDimensions.width)
      : videoDimensions.width;
    const height = crop
      ? Math.min(videoDimensions.height, faceMaskDimensions.height)
      : videoDimensions.height;

    logger.info(`onSelfieCapture :: Screen resolution`, getScreenResolution());
    logger.info(`onSelfieCapture :: Image dimensions`, width, height);
    logger.info(`onSelfieCapture :: scale`, scale);
    logger.info(
      `onSelfieCapture :: Reported Video Element Dimensions`,
      videoDimensions,
    );
    logger.info(
      `onSelfieCapture :: Reported Face Mask Dimensions`,
      faceMaskDimensions,
    );

    this.resultPhotoEl.width = width;
    this.resultPhotoEl.height = height;

    const context = this.resultPhotoEl.getContext('2d');

    if (mirrored) {
      // Ensure image is mirrored:
      context.translate(width, 0);
      context.scale(-1, 1);
    }

    const translate = {
      x: Math.max(0, (videoDimensions.width - width) / 2),
      y: Math.max(0, (videoDimensions.height - height) / 2),
    };

    logger.info(`onSelfieCapture :: Translate`, translate);

    context.drawImage(
      this.cameraStreamEl,
      translate.x,
      translate.y,
      width,
      height,
      0,
      0,
      width,
      height,
    );

    this.setState(
      () => ({
        selfie: this.resultPhotoEl.toDataURL('image/png'),
        countdownStarted: false,
      }),
      () => {
        const { selfie: latestSelfie } = this.state;

        if (onSelfieTaken) {
          logger.info(
            `onSelfieCapture :: passing over to onSelfieTaken `,
            latestSelfie,
          );
          onSelfieTaken(dataURItoFile(latestSelfie, `${uuidv4()}.png`));
        }
      },
    );
  };

  onSelfieConfirmed = () => {
    const { onSelfieConfirmed } = this.props;
    const { selfie } = this.state;

    if (onSelfieConfirmed) {
      onSelfieConfirmed(dataURItoFile(selfie, `${uuidv4()}.png`));
    }
  };

  render() {
    const {
      selfie,
      devices,
      loadingDevices,
      client,
      currentDevice,
      mirrored,
      countdownStarted,
    } = this.state;

    const {
      onClose,
      countdown,
      renderPermissionButton,
      renderCaptureAction,
      includeTipsCopy,
      faceMaskDisabled = false,
      faceMaskProps = {},
    } = this.props;

    const canvasClasses = classNames({
      'selfie-result': true,
      'd-none': !selfie,
    });

    // We want selfieCapture items to be rendered since we use the ref items from it.
    const selfieCaptureContainerClasses = classNames({
      'selfie-capture-container container-fluid content-wrap-gray':
        !loadingDevices,
      'd-none': loadingDevices,
    });

    const loadingScreenClasses = classNames({
      'selfie-capture-loading-screen wrapper-main d-flex align-content-center justify-content-center w-100':
        loadingDevices,
      'd-none': !loadingDevices,
    });

    const videoElementClasses = classNames({
      'selfie-stream': true,
      'selfie-stream-mirrored': mirrored,
      'd-none': !!selfie,
    });

    return (
      <Fragment>
        <ScrollToTop />
        <div className={loadingScreenClasses}>
          {!faceMaskDisabled && (
            <SelfieCaptureFaceMask
              id="loading-selfie-mask"
              {...faceMaskProps}
            />
          )}
          <div className="d-flex flex-column justify-content-center align-items-center">
            <CameraIcon color="#0d84fe" size={34} className="mb-2" />
            <p className="text-center text-primary text-black">
              Activate camera <br /> to take a photo
            </p>
          </div>
        </div>
        <Fragment>
          {renderPermissionButton?.({
            loadingDevices,
            getCameraPermission: this.getCameraPermission,
          })}
        </Fragment>
        <div className={selfieCaptureContainerClasses}>
          <div className="selfie-capture-video-container">
            {!selfie && includeTipsCopy && (
              <span className="selfie-capture-video-notes">
                Tap the button below, look here, and smile! I&rsquo;ll use your
                selfie to find and deliver your photos.
              </span>
            )}
            <video
              playsInline
              autoPlay
              className={videoElementClasses}
              ref={ref => {
                this.cameraStreamEl = ref;
              }}
              muted
            />
          </div>
          {!!client &&
            !!currentDevice &&
            !loadingDevices &&
            !faceMaskDisabled && <SelfieCaptureFaceMask {...faceMaskProps} />}
          <div className="selfie-capture-canvas-container">
            <canvas
              className={canvasClasses}
              ref={ref => {
                this.resultPhotoEl = ref;
              }}
            />
          </div>
          {selfie ? (
            <div className="selfie-captured-actions">
              <Button
                btnBlock
                onClick={this.onSelfieConfirmed}
                className="m-0 mr-1 selfie-capture-confirm"
              >
                This is Good!
              </Button>
              <Button
                btnBlock
                btnOutline
                onClick={this.onSelfieCapture}
                className="m-0 ml-1 selfie-capture-retake"
              >
                Retake Photo
              </Button>
            </div>
          ) : (
            <Fragment>
              {onClose && (
                <Button
                  btnStyle="reset"
                  className="selfie-change-close-action"
                  onClick={() => {
                    onClose();
                  }}
                >
                  &times;
                </Button>
              )}
              <Button
                btnStyle="reset"
                className="selfie-change-mirror-action"
                onClick={() => {
                  this.setState(() => ({ mirrored: !mirrored }));
                }}
              >
                <AngleRightIcon color="#fff" /> <span className="divisor" />
                <AngleLeftIcon color="#fff" />
              </Button>
              <div className="selfie-capture-action">
                {renderCaptureAction ? (
                  renderCaptureAction(this.onSelfieCapture)
                ) : (
                  <Button
                    data-testid="capture-action"
                    btnStyle="reset"
                    disabled={countdownStarted}
                    onClick={this.onSelfieCapture}
                  />
                )}
              </div>
              {devices.length > 1 && (
                <Button
                  btnStyle="reset"
                  className="selfie-change-camera-action"
                  onClick={this.onDeviceChange}
                >
                  <img src={getAsset('/icon-restore.svg')} alt="swap camera" />
                </Button>
              )}
              {countdownStarted && (
                <SelfieCaptureCountdown
                  step={500}
                  from={countdown}
                  onFinished={this.onSelfieCapture}
                  render={remaining => {
                    return <div className="selfie-countdown">{remaining}</div>;
                  }}
                />
              )}
            </Fragment>
          )}
        </div>
      </Fragment>
    );
  }
}

export { SelfieCapture };

const mapDispatchToProps = {
  requestConfirm: requestConfirmAction,
};

const SelfieCaptureContainer = compose(connect(null, mapDispatchToProps))(
  SelfieCapture,
);

export default SelfieCaptureContainer;
